
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { formatLocal, utc } from 'app/shared/util/date.util';

import { DialogService } from '../../../shared/dialog/dialog.service';
import { PageManageDialogComponent } from '../../../core/page-manage-dialog.component';
import { OrderServiceAcceptDateGridService } from '../../core/order-service-accept-date-grid.service';
import { INVENTORY_STATUS_ENUM } from '../../../inventory/core/inventory-status.enum';
import { ORDER_SERVICE_STATUS } from '../../../order-service/core/order-service-status.enum';
import { ORDER_STATUS } from '../../../order/core/order-status.enum';
import { OrderServiceService } from '../../../order-service/core/order-service.service';
import { NotesService } from '../../../shared/notes/core/notes.service';
import { Notes } from '../../../shared/notes/core/notes';
import { InventoryService } from '../../../inventory/core/inventory.service';
import { UserService } from '../../../user/core/user.service';
import { AlertService } from '../../../shared/alert/alert.service';
import { IMessagesResourceService, ResourcesService } from '../../../core/resources/resources.service';
import { alertOrderServiceSaveError } from '../../core/order-service-save';

/**
 * CAS-1269 tri-state service test rule. Each side's *_test_passed column is a nullable
 * boolean: null/undefined = not recorded yet, false = recorded as failed, true = passed.
 * (The old dialog wrote only the side being recorded, so false never meant "unanswered".)
 *
 *   - any side failed        -> TEST_FAILED (61)
 *   - both sides passed      -> TEST_PASSED (62)
 *   - otherwise (incomplete) -> SERVICE_TEST (60), the pending state
 */
export function deriveServiceTestStatus(vendorPassed: boolean | null | undefined,
                                        customerPassed: boolean | null | undefined): number {
  if (vendorPassed === false || customerPassed === false) {
    return ORDER_SERVICE_STATUS.TEST_FAILED;
  }
  if (vendorPassed === true && customerPassed === true) {
    return ORDER_SERVICE_STATUS.TEST_PASSED;
  }
  return ORDER_SERVICE_STATUS.SERVICE_TEST;
}

@Component({
  selector: 'app-service-test',
  templateUrl: './service-test.component.html',
  styleUrls: ['./service-test.component.css'],
  standalone: false
})
export class ServiceTestComponent extends PageManageDialogComponent implements OnInit {
  readonly ORDER_SERVICE_STATUS_ENUM = ORDER_SERVICE_STATUS;
  readonly ORDER_STATUS_ENUM = ORDER_STATUS;
  readonly INVENTORY_STATUS = INVENTORY_STATUS_ENUM;

  @ViewChild('grid') grid: DxDataGridComponent;
  columns = [];

  readonly dateFormat = 'MM/dd/yyyy';

  note: string;
  public test_date: Date;
  private saveBuffer: any;

  public orderService;
  public minAllowedDate;

  hasMrcNrc: boolean;
  mrc: number;
  nrc: number;
  service_test_date_history: any;
  isServiceTestChanged: boolean = false;
  saving = false;

  /** Status the service had when the dialog opened - what Cancel leaves it at. */
  initialState: number;
  private pendingNotes: string[] = [];
  private currentUserName: string;

  readonly MESSAGES_MODULE: string = 'order';
  messages: IMessagesResourceService;

  NO_MRC_NRC_MSG = 'In order to pass, MRC and NRC has to be specified.';

  readonly TEST_TYPE: any = {
    VENDOR: 'Vendor',
    CUSTOMER: 'Customer'
  };

  constructor(
    public formBuilder: FormBuilder,
    public dialogService: DialogService,
    public dialogRef: MatDialogRef<ServiceTestComponent>,
    public orderServiceService: OrderServiceService,
    public inventoryService: InventoryService,
    public notesService: NotesService,
    public orderServiceAcceptDateGridService: OrderServiceAcceptDateGridService,
    public userService: UserService,
    public alertService: AlertService,
  ) {
    super(dialogService, dialogRef);
    this.messages = ResourcesService.messages(this.MESSAGES_MODULE);
  }

  ngOnInit() {
    // Work on a private copy: nothing reaches the caller's object unless OK saves it.
    this.orderService = { ...this.orderService };
    this.initialState = this.orderService.state;
    this.userService.me().subscribe({
      next: (user) => this.currentUserName = user && user.full_name,
      error: () => {}
    });

    this.getMinDate()
    this.form = this.formBuilder.group({
      note: [null, Validators.minLength(20)],
      note_customer: [null, Validators.minLength(20)],
      customer_test_date: [null],
      test_date: [null]
    });

    this.hasMrcNrc = !!(this.mrc != null && this.nrc != null);

    if (this.service_test_date_history && this.service_test_date_history.length) {
      this.initGrid();
    }
  }

  initGrid() {
    this.columns = this.orderServiceAcceptDateGridService.columns();
    setTimeout(() => {
      if (!this.grid) {
        return;
      }
      this.orderServiceAcceptDateGridService.create(this.grid.instance, {
        height: '250px',
        allowColumnResizing: false
      });
    });
  }

  approve({ value, valid }: { value: any, valid: boolean }, testType) {
    if (testType === this.TEST_TYPE.VENDOR) {
      this.saveBuffer = {
        approved: true,
        type: this.TEST_TYPE.VENDOR,
        test_date: value.test_date ? formatLocal(value.test_date, this.dateFormat) : null,
        note: value.note,
      };
    } else if (testType === this.TEST_TYPE.CUSTOMER) {
      this.saveBuffer = {
        approved: true,
        type: this.TEST_TYPE.CUSTOMER,
        note: value.note_customer,
        customer_test_date: value.customer_test_date ? formatLocal(value.customer_test_date, this.dateFormat) : null,
      };
    }
    this.recordResult();
  }

  reject({ value, valid }: { value: any, valid: boolean }, testType) {
    if (testType === this.TEST_TYPE.VENDOR) {
      this.saveBuffer = {
        rejected: true,
        type: this.TEST_TYPE.VENDOR,
        test_date: formatLocal(value.test_date, this.dateFormat),
        note: value.note,
      };
    } else if (testType === this.TEST_TYPE.CUSTOMER) {
      this.saveBuffer = {
        rejected: true,
        type: this.TEST_TYPE.CUSTOMER,
        note: value.note_customer,
        customer_test_date: formatLocal(value.customer_test_date, this.dateFormat),
      };
    }
    this.recordResult();
  }

  /**
   * Records one side's Pass/Fail on the dialog's working copy only. Nothing is written
   * until OK (CAS-1269): Cancel must be able to throw the whole session away, so a
   * Pass/Fail click can no longer persist on its own.
   */
  recordResult() {
    const passed = this.saveBuffer.approved === true;

    // The buffer's dates are already formatted MM/dd/yyyy by approve()/reject(). They must
    // not go through formatLocal() again: it parses with parseISO, so a second pass throws
    // RangeError("Invalid time value") - which aborted every Pass/Fail click before anything
    // was saved (the "entries are not captured" half of CAS-1269).
    if (this.saveBuffer.test_date) {
      this.orderService.test_passed = passed;
      this.orderService.service_test_vendor_date = this.saveBuffer.test_date;
      this.orderService.vendor_test_passed = passed;
      this.orderService.vendor_changed = true;
    }
    if (this.saveBuffer.customer_test_date) {
      this.orderService.service_test_customer_date = this.saveBuffer.customer_test_date;
      this.orderService.test_passed = passed;
      this.orderService.customer_test_passed = passed;
      this.orderService.vendor_changed = false;
    }

    this.orderService.state = deriveServiceTestStatus(
      this.orderService.vendor_test_passed,
      this.orderService.customer_test_passed
    );
    this.orderService.note = this.saveBuffer.note;

    const entry = this.generateServiceTestHistoryEntry(this.orderService);
    const history = this.service_test_date_history && this.service_test_date_history.length
      ? this.service_test_date_history
      : [];
    this.service_test_date_history = this.orderService.service_test_date_history = [entry, ...history];

    if (this.saveBuffer.note && this.saveBuffer.note.length > 0) {
      const msg = passed ? 'Service Test - Test Passed' : 'Service Test - Test Failed';
      this.pendingNotes.push(`${msg}: ${this.saveBuffer.note}`);
    }

    this.isServiceTestChanged = true;
    this.initGrid();
    if (this.grid) {
      this.grid.instance.getScrollable().scrollTo(0);
    }
    this.form.reset();
  }

  public generateServiceTestHistoryEntry(orderService): any {
    const newServiceTestHistoryEntry = {
      date: null,
      pass: orderService.test_passed,
      type: this.saveBuffer.type,
      changed_at: utc(),
      note: this.saveBuffer.note,
      changed_by: this.currentUserName || ``,
    };

    if (this.saveBuffer.test_date) {
      newServiceTestHistoryEntry.date = this.saveBuffer.test_date;
    }

    if (this.saveBuffer.customer_test_date) {
      newServiceTestHistoryEntry.date = this.saveBuffer.customer_test_date;
    }

    return newServiceTestHistoryEntry;
  }

  public isPassDisabled(field) {
    let result = !this.hasMrcNrc || !this.form.controls[field].value || !this.form.controls[field].valid;
    return result;
  }

  public isFailDisabled(field, noteField) {
    let result = !this.form.controls[field].value || !this.form.controls[field].valid
      || !this.form.controls[noteField].value || !this.form.controls[noteField].valid;
    return result;
  }

  public addNote(noteMessage) {
    let note = new Notes({
      entity_id: this.orderService.order_id,
      entity_type: 20,
      content: noteMessage
    });

    // BaseService.create returns a cold HttpClient observable - without a subscriber the
    // note was never posted.
    this.notesService.create(note).subscribe({ error: () => {} });
  }

  /**
   * OK: applies the tri-state rule to whatever is on record (61 only on an actual
   * failure, 62 only when both sides passed, otherwise 60 pending), saves once, and
   * closes with the saved service so the step-6 afterClosed cascade re-evaluates the
   * stepper. Re-opening and pressing OK on a service left at 61 by the old both-sides
   * rule recomputes it, so a one-sided pass is moved back to pending.
   */
  ok() {
    if (this.saving) {
      return;
    }

    const status = deriveServiceTestStatus(this.orderService.vendor_test_passed, this.orderService.customer_test_passed);

    if (!this.isServiceTestChanged && status === this.initialState) {
      this.closeDialog(this.orderService, true);
      return;
    }

    this.orderService.state = status;
    this.saving = true;

    this.orderServiceService.update(this.orderService.id, this.orderService)
      .subscribe({
        next: (saved) => {
          this.pendingNotes.forEach((message) => this.addNote(message));
          this.pendingNotes = [];
          this.closeDialog(saved || this.orderService, true);
        },
        // The save failed (the server now answers a failed update with an error status - CAS-1269
        // review): stay open with everything recorded so the user can retry or Cancel, post no
        // notes (nothing was saved), and say so.
        error: (error) => {
          this.saving = false;
          alertOrderServiceSaveError(this, error, 'ORDER_SERVICE_TEST_SAVE_ERROR');
        }
      });
  }

  /**
   * Cancel: discards everything recorded in this session. Nothing was written while the
   * dialog was open, so closing without a result leaves the service at the status it
   * had when the dialog opened, and the afterClosed cascade does nothing.
   *
   * Users used to close this dialog with Cancel after each Pass/Fail had already saved, so
   * when results were recorded in this session Cancel asks first; declining keeps the dialog
   * (and the recorded results) open. The data-lock timer does not come through here: it
   * calls closeDialog() directly and still discards.
   */
  cancel() {
    if (!this.isServiceTestChanged) {
      this.discardAndClose();
      return;
    }

    this.dialogService.confirm({
      bodyText: this.messages.get('ORDER_SERVICE_TEST_DISCARD_CONFIRM'),
      confirmButtonLabel: 'Discard'
    })
      .afterClosed()
      .subscribe((discard) => {
        if (discard) {
          this.discardAndClose();
        }
      });
  }

  private discardAndClose() {
    this.pendingNotes = [];
    this.closeDialog(undefined, true);
  }

  getMinDate(){
    this.minAllowedDate = formatLocal(this.orderService.install_date, 'yyyy-MM-dd');
  }
}
